import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import PrimeModal from '@/components/primeModal'
import { useTranslation } from 'react-i18next'
import { TreeSelect, Form, Input, App, Spin } from 'antd'
import { formatTreeSelect, findParentKeys } from './dictTree/utils'
import { debounce } from 'lodash'

interface MoveDictProps {
  onSubmit?: (values: any) => void
  queryDictTree: (search: string) => any
  disabledKey: string
}

const MoveDictItem = (
  { onSubmit, queryDictTree, disabledKey }: MoveDictProps,
  ref: any
) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [visible, setVisible] = useState(false)
  const { message } = App.useApp()
  const [expandedKeys, setExpandedKeys] = useState<any>([])
  const [treeData, setTreeData] = useState<any[]>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const debouncedSetSearchValue = debounce(setSearchValue, 500)

  const fetchTreeData = async (search?: string) => {
    setLoading(true)
    const result = await queryDictTree(search || '')
    setTreeData(result)
    if (search) {
      setExpandedKeys(findParentKeys(result, 'id'))
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!visible) return
    fetchTreeData(searchValue)
  }, [visible, searchValue])

  const open = (record: any) => {
    form.setFieldsValue(record)
    if (record?.keyPath) {
      const keys = record.keyPath.split('/').map((n) => parseInt(n))
      setExpandedKeys(keys.length > 1 ? keys.slice(0, keys.length - 1) : keys)
    }
    setVisible(true)
  }

  const close = () => {
    form.resetFields()
    setVisible(false)
  }

  useImperativeHandle(ref, () => ({
    open,
    close,
  }))

  const submit = async (values: any) => {
    if (typeof onSubmit === 'function') {
      const res: any = await onSubmit(values)
      if (res?.code === 300 && res?.message) {
        form.setFields([{ name: 'directoryId', errors: [res.message] }])
        return
      }
      close()
      message.success(t('ikg.success', { action: t('ikg.move') }))
    }
  }

  return (
    <PrimeModal
      title={t('ikg.move')}
      width={640}
      open={visible}
      onCancel={close}
      onOk={() => form.submit()}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="directoryId"
          label={t('ikg.moveToDir')}
          rules={[
            {
              required: true,
              message: t('ikg.movePlaceholder'),
            },
          ]}
        >
          <TreeSelect
            className="custom-tree-select"
            showSearch
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder={t('ikg.movePlaceholder')}
            treeData={formatTreeSelect({
              tree: treeData,
              disabledKey,
              disableSub: false,
            })}
            filterTreeNode={false}
            treeNodeLabelProp="fullPath"
            allowClear
            treeExpandedKeys={expandedKeys}
            onTreeExpand={(keys) => setExpandedKeys(keys)}
            onSearch={debouncedSetSearchValue}
            dropdownRender={(node: any) => {
              return <Spin spinning={loading}>{node}</Spin>
            }}
          />
        </Form.Item>
        <Form.Item hidden name="itemId">
          <Input />
        </Form.Item>
      </Form>
    </PrimeModal>
  )
}

export default forwardRef(MoveDictItem)
